/**
 * A classroom's cover, as bytes the device can keep.
 *
 * A classroom has no record behind it — the whole world of classrooms lives on
 * the device (`#/state/classrooms`) — and the account's repo cannot stand in for
 * one: an uploaded blob that no record references is never *served*, so a cover
 * kept as an address is a cover nobody can see, its author included. The picture
 * therefore travels with the classroom, as a data URI, which is exactly as
 * visible as the classroom itself is: on this device, and nowhere else.
 *
 * That makes the two numbers here a storage decision rather than a taste one. The
 * band is 150px tall and never drawn wider than a page's own column, so 1200px is
 * already several times what a screen can show — and a cover is not a post image:
 * it is kept *in* the store, beside everything else the app keeps, in a world where
 * a browser gives the whole origin a few megabytes and base64 spends four characters
 * on every three bytes. A third of a megabyte of characters per classroom is the
 * honest ceiling: a dozen of them fill a browser's storage, and a cover that ignored
 * this would take the whole store down with it (a write that does not fit is a write
 * that fails), which is a far worse failure than a slightly softer band.
 */

/** The widest a cover is ever kept at. */
export const COVER_MAX_WIDTH = 1200

/**
 * How many bytes of JPEG one cover may be — which the store holds as about a third
 * more than this in characters.
 */
export const COVER_MAX_BYTES = 180_000

/**
 * The size a cover is kept at: the picture's own shape, no wider than the band.
 *
 * The editor has cropped it already, so the shape is whatever the person chose —
 * but the *scale* is a phone photo's, and a cover is never drawn at a phone photo's
 * size. A picture already narrower than the band is left as it is rather than blown
 * up: enlarging bytes that exist adds nothing that can be seen, and costs storage
 * for the nothing.
 */
export function coverSize(img: {width: number; height: number}): {
  width: number
  height: number
} {
  if (!(img.width > 0) || !(img.height > 0)) {
    // A picture whose size did not survive the picker still has to become a band,
    // and the band is the only shape a cover has: the editor's own 3:1.
    return {width: COVER_MAX_WIDTH, height: Math.round(COVER_MAX_WIDTH / 3)}
  }
  const width = Math.min(Math.round(img.width), COVER_MAX_WIDTH)
  return {
    width,
    height: Math.max(1, Math.round(width * (img.height / img.width))),
  }
}
